<template>
    <el-popover placement="top" trigger="hover" :width="280" popper-class="mobile-popover" :show-arrow="true"
        :offset="12" @show="onShow">
        <template #reference>
            <slot />
        </template>

        <div class="mobile-popover__body">
            <!-- 系统需求说明 -->
            <p class="mobile-popover__req">{{ req }}</p>

            <!-- 二维码：扫码下载 -->
            <div class="mobile-popover__qr">
                <div class="mobile-popover__qr-code">
                    <canvas ref="canvas" class="mobile-popover__qr-img" />
                </div>
                <p class="mobile-popover__qr-tip">{{ qrTip }}</p>
            </div>

            <!-- 下载按钮 -->
            <button class="mobile-popover__btn" type="button" @click="download">
                <component :is="platformIcon" class="mobile-popover__logo" />
                <span>{{ btnText }}</span>
            </button>
        </div>
    </el-popover>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElPopover } from 'element-plus'
import 'element-plus/es/components/popover/style/css'
import QrCode from 'qrcode'
import Android from '@/assets/icons/android.svg'
import Apple from '@/assets/icons/apple.svg'
import Smartphone from '@/assets/icons/smartphone.svg'
import { checkInnerBrowser } from '@/utils/browser_env'

type Platform = 'android' | 'ios' | 'smart'

const { t } = useI18n()
const canvas = ref<HTMLCanvasElement>()

const platformIcon = computed(() => {
    switch (props.platform) {
        case 'android': return Android
        case 'ios': return Apple
        case 'smart': return Smartphone
    }
})
const btnText = computed(() => {
    switch (props.platform) {
        case 'android': return t('download.android')
        case 'ios': return t('download.ios')
        case 'smart': return t('download.smart')
    }
})
const qrTip = computed(() => {
    switch (props.platform) {
        case 'android': return t('download.qrTipAndroid')
        case 'ios': return t('download.qrTipIos')
        case 'smart': return t('download.qrTipSmart')
    }
})

const props = defineProps<{
    platform: Platform
    url: string
    req: string
}>()

function onShow(): void {
    nextTick(drawQRCode)
}

function drawQRCode(): void {
    if (!canvas.value || !props.url) return
    QrCode.toCanvas(canvas.value, props.url, { width: 120 })
}

function download(): void {
    // iOS 直接打开，不检测内置浏览器
    if (props.platform !== 'ios' && checkInnerBrowser(t('browserTip.install'))) {
        return
    }

    if (props.url && props.url !== '#') {
        const link = document.createElement('a')
        link.href = props.url
        link.target = '_blank'
        link.rel = 'noopener noreferrer'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }
}
</script>

<style lang="scss">
@use '@/assets/styles/variables' as *;

$orange: #ff6d00;

/* el-popover 内容被 teleport 到 body，需用全局样式 */
.mobile-popover.el-popper {
    padding: 1.25rem;
    border-radius: 16px;
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.18);
}

.mobile-popover__body {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
}

.mobile-popover__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
    height: 42px;
    background: $orange;
    color: #fff;
    font-size: 0.875rem;
    font-weight: 600;
    border: none;
    border-radius: 999px;
    cursor: pointer;
    transition: background 0.15s, transform 0.15s;

    &:hover {
        background: #e85d00;
        transform: translateY(-1px);
    }

    &:active {
        transform: translateY(0);
    }
}

.mobile-popover__logo {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
}

.mobile-popover__req {
    margin: 0;
    font-size: 0.75rem;
    color: $color-text-secondary;
    text-align: center;
    line-height: 1.5;
}

.mobile-popover__qr {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
}

.mobile-popover__qr-code {
    width: 130px;
    height: 130px;
    background: #fff;
    border-radius: 10px;
    padding: 6px;
    box-sizing: border-box;
    border: 1px solid $color-border;
}

.mobile-popover__qr-img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: contain;
}

.mobile-popover__qr-tip {
    margin: 0;
    font-size: 0.75rem;
    color: $color-text-secondary;
    text-align: center;
}
</style>
